import { useState } from 'react';
import { WifiOff } from 'lucide-react';
import { Button, EmptyState, FullPageOverlay } from '@printtat/ui';

interface ConnectionErrorProps {
  // The message surfaced by AuthContext (e.g. "Unable to reach the server…").
  message: string;
  // Re-attempt the session bootstrap. Wired to checkAuth() so a transient
  // 429/5xx/network blip recovers in place — without bouncing a still-valid
  // session to the sign-in page.
  onRetry: () => Promise<unknown> | unknown;
}

// Full-page connectivity screen shown by the route guards when the session
// bootstrap fails transiently (user === null but error !== null). The refresh
// cookie may still be valid, so we offer a retry instead of redirecting to
// sign-in, which would read as a false logout.
export const ConnectionError = ({ message, onRetry }: ConnectionErrorProps) => {
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await onRetry();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <FullPageOverlay>
      <EmptyState
        icon={WifiOff}
        title="Can't reach the server"
        description={message}
        action={
          <Button onClick={handleRetry} loading={retrying}>
            Try again
          </Button>
        }
      />
    </FullPageOverlay>
  );
};
